import { apiClient, ApiResponse } from './apiClient';

export interface Review {
  id: number;
  user_id: number;
  property_id: number;
  booking_id?: number;
  rating: number;
  comment: string;
  status: 'pending' | 'approved' | 'rejected';
  is_featured: boolean;
  created_at: string;
  updated_at: string;
  // Related data
  user?: {
    id: number;
    name: string;
    email: string;
    avatar?: string;
  };
  property?: {
    id: number;
    name: string;
    type: string;
  };
  booking?: {
    id: number;
    check_in: string;
    check_out: string;
  };
}

export interface ReviewFilters {
  search?: string;
  rating?: number;
  status?: 'pending' | 'approved' | 'rejected';
  is_featured?: boolean;
  property_id?: number;
  user_id?: number;
  page?: number;
  limit?: number;
}

export interface ReviewListResponse {
  reviews: Review[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface CreateReviewRequest {
  bookingId: string;
  rating: number;
  comment: string;
  media?: string[];
}

export interface UpdateReviewRequest {
  status?: 'pending' | 'approved' | 'rejected';
  is_featured?: boolean;
  comment?: string;
}

export interface ReviewStats {
  total_reviews: number;
  pending_reviews: number;
  approved_reviews: number;
  rejected_reviews: number;
  featured_reviews: number;
  average_rating: number;
}

export class ReviewsService {
  private baseUrl = '/admin/reviews';

  // Get all reviews with filters
  async getReviews(filters: ReviewFilters = {}): Promise<ApiResponse<ReviewListResponse>> {
    try {
      console.log('🔧 ReviewsService.getReviews called:', { filters });
      
      const params = new URLSearchParams();
      
      if (filters.search) params.append('search', filters.search);
      if (filters.rating) params.append('rating', filters.rating.toString());
      if (filters.status) params.append('status', filters.status);
      if (filters.is_featured !== undefined) params.append('is_featured', filters.is_featured.toString());
      if (filters.property_id) params.append('property_id', filters.property_id.toString());
      if (filters.user_id) params.append('user_id', filters.user_id.toString());
      if (filters.page) params.append('page', filters.page.toString());
      if (filters.limit) params.append('limit', filters.limit.toString());

      const queryString = params.toString();
      const url = queryString ? `${this.baseUrl}?${queryString}` : this.baseUrl;
      
      console.log('🔧 Making GET request to:', url);
      console.log('🔧 Query parameters:', Object.fromEntries(params));
      
      const response = await apiClient.get<Review[]>(url);
      
      console.log('🔧 ReviewsService.getReviews response:', response);
      console.log('🔧 Response success:', response.success);
      console.log('🔧 Response data type:', typeof response.data);
      console.log('🔧 Response data:', response.data);
      
      if (response.success && response.data) {
        // Handle different response formats
        let reviews: Review[] = [];
        let total = 0;
        let page = filters.page || 1;
        let limit = filters.limit || 10;
        
        console.log('🔧 Processing response data:', response.data);
        console.log('🔧 Is array?', Array.isArray(response.data));
        console.log('🔧 Has reviews property?', !!(response.data as any).reviews);
        
        if (Array.isArray(response.data)) {
          // Direct array response
          console.log('🔧 Using direct array format');
          reviews = response.data;
          total = reviews.length;
        } else if ((response.data as any).reviews) {
          // Paginated response
          console.log('🔧 Using paginated format');
          const paginatedData = response.data as any;
          reviews = paginatedData.reviews || [];
          total = paginatedData.total || reviews.length;
          page = paginatedData.page || page;
          limit = paginatedData.limit || limit;
        } else {
          // Try to extract from nested data structure
          console.log('🔧 Trying nested data extraction');
          const nestedData = (response.data as any).data;
          if (Array.isArray(nestedData)) {
            reviews = nestedData;
            total = reviews.length;
          } else if (nestedData && nestedData.reviews) {
            reviews = nestedData.reviews || [];
            total = nestedData.total || reviews.length;
            page = nestedData.page || page;
            limit = nestedData.limit || limit;
          }
        }
        
        console.log('🔧 Final extracted reviews:', reviews);
        console.log('🔧 Final reviews count:', reviews.length);
        
        const totalPages = Math.ceil(total / limit);
        
        const listResponse: ReviewListResponse = {
          reviews,
          total,
          page,
          limit,
          totalPages
        };
        
        return {
          success: true,
          data: listResponse,
          message: 'Reviews retrieved successfully'
        };
      }
      
      return {
        success: false,
        data: {
          reviews: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        },
        message: 'No reviews data received'
      };
    } catch (error: any) {
      console.error('🔧 ReviewsService.getReviews error:', error);
      
      return {
        success: false,
        data: {
          reviews: [],
          total: 0,
          page: 1,
          limit: 10,
          totalPages: 0
        },
        message: error.message || 'Error fetching reviews'
      };
    }
  }

  // Get single review by ID
  async getReview(id: number): Promise<ApiResponse<Review>> {
    try {
      console.log('🔧 ReviewsService.getReview called:', { id });
      
      const response = await apiClient.get<Review>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 ReviewsService.getReview response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 ReviewsService.getReview error:', error);
      throw error;
    }
  }

  // Create new review
  async createReview(data: CreateReviewRequest): Promise<ApiResponse<Review>> {
    try {
      console.log('🔧 ReviewsService.createReview called:', { data });
      
      // Prepare data for API
      const reviewData = {
        bookingId: data.bookingId,
        rating: Number(data.rating),
        comment: data.comment.trim(),
        media: data.media || []
      };
      
      console.log('🔧 ReviewsService.createReview prepared data:', reviewData);
      console.log('🔧 Making POST request to:', '/reviews');
      console.log('🔧 Request payload:', JSON.stringify(reviewData, null, 2));
      
      // Use /reviews endpoint (not /admin/reviews for creation)
      const response = await apiClient.post<Review>('/reviews', reviewData);
      
      console.log('🔧 ReviewsService.createReview response:', response);
      console.log('🔧 Response status:', response.success);
      console.log('🔧 Response data:', response.data);
      
      return response;
    } catch (error: any) {
      console.error('🔧 ReviewsService.createReview error:', error);
      console.error('🔧 Error response data:', error.response?.data);
      console.error('🔧 Error response status:', error.response?.status);
      
      let errorMessage = 'Error creating review';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        // Handle validation errors array
        const validationErrors = error.response.data.errors.map((err: any) => {
          if (typeof err === 'string') return err;
          if (err.msg && err.path) return `${err.path}: ${err.msg}`;
          return err.msg || err.message || 'Validation error';
        });
        errorMessage = validationErrors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Update review (status, featured, etc.)
  async updateReview(id: number, data: UpdateReviewRequest): Promise<ApiResponse<Review>> {
    try {
      console.log('🔧 ReviewsService.updateReview called:', { id, data });
      
      // Prepare data for API
      const updateData: any = {};
      
      if (data.status !== undefined && data.status !== null) {
        updateData.status = data.status;
        console.log('🔧 Adding status:', updateData.status);
      }
      if (data.is_featured !== undefined && data.is_featured !== null) {
        updateData.is_featured = data.is_featured;
        console.log('🔧 Adding is_featured:', updateData.is_featured);
      }
      if (data.comment !== undefined && data.comment !== null && data.comment.trim() !== '') {
        updateData.comment = data.comment.trim();
        console.log('🔧 Adding comment:', updateData.comment);
      }
      
      console.log('🔧 ReviewsService.updateReview prepared data (PATCH):', updateData);
      console.log('🔧 Making PATCH request to:', `${this.baseUrl}/${id}`);
      console.log('🔧 Request payload:', JSON.stringify(updateData, null, 2));
      
      // Check if we have any data to send
      if (Object.keys(updateData).length === 0) {
        console.warn('🔧 WARNING: No data to update! updateData is empty');
        return {
          success: false,
          data: null as any,
          message: 'No data provided for update'
        };
      }
      
      const response = await apiClient.patch<Review>(`${this.baseUrl}/${id}`, updateData);
      
      console.log('🔧 ReviewsService.updateReview response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 ReviewsService.updateReview error:', error);
      
      let errorMessage = 'Error updating review';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const validationErrors = error.response.data.errors.map((err: any) => {
          if (typeof err === 'string') return err;
          if (err.msg && err.path) return `${err.path}: ${err.msg}`;
          return err.msg || err.message || 'Validation error';
        });
        errorMessage = validationErrors.join(', ');
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      return {
        success: false,
        data: null as any,
        message: errorMessage,
        errors: error.response?.data?.errors || []
      };
    }
  }

  // Delete review
  async deleteReview(id: number): Promise<ApiResponse<void>> {
    try {
      console.log('🔧 ReviewsService.deleteReview called:', { id });
      
      const response = await apiClient.delete<void>(`${this.baseUrl}/${id}`);
      
      console.log('🔧 ReviewsService.deleteReview response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 ReviewsService.deleteReview error:', error);
      throw error;
    }
  }

  // Toggle review featured status
  async toggleFeatured(id: number, is_featured: boolean): Promise<ApiResponse<Review>> {
    try {
      console.log('🔧 ReviewsService.toggleFeatured called:', { id, is_featured });
      
      return this.updateReview(id, { is_featured });
    } catch (error: any) {
      console.error('🔧 ReviewsService.toggleFeatured error:', error);
      
      return {
        success: false,
        data: null as any,
        message: 'Error updating review featured status'
      };
    }
  }

  // Bulk update reviews
  async bulkUpdateReviews(ids: number[], data: UpdateReviewRequest): Promise<ApiResponse<void>> {
    try {
      console.log('🔧 ReviewsService.bulkUpdateReviews called:', { ids, data });
      
      const response = await apiClient.patch<void>(`${this.baseUrl}/bulk`, {
        ids,
        ...data
      });
      
      console.log('🔧 ReviewsService.bulkUpdateReviews response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 ReviewsService.bulkUpdateReviews error:', error);
      throw error;
    }
  }

  // Get review statistics (if endpoint exists)
  async getReviewStats(): Promise<ApiResponse<ReviewStats>> {
    try {
      console.log('🔧 ReviewsService.getReviewStats called');
      
      const response = await apiClient.get<ReviewStats>(`${this.baseUrl}/stats`);
      
      console.log('🔧 ReviewsService.getReviewStats response:', response);
      
      return response;
    } catch (error: any) {
      console.error('🔧 ReviewsService.getReviewStats error:', error);
      throw error;
    }
  }
}

export const reviewsService = new ReviewsService();
